import {
  LTLShiplifyDecision,
  LTLShiplifyLookupMetadata,
  LTLShiplifyRecommendation,
} from 'lib/api/getLTLQuickQuotes';
import {
  LTLAccessorial,
  QuoteFormStop,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { StopTypes } from 'types/Appointment';
import { Undef } from 'types/UtilityTypes';

import {
  applyParsedStopAddressToQuoteFormStop,
  parseStopAddressFromLocation,
} from '../utils/parseStopAddressFromLocation';
import {
  SHIPLIFY_CODE_LABELS,
  SHIPLIFY_CODE_NAME_TERMS,
  SHIPLIFY_CONFIDENCE_ACCURACY,
  SHIPLIFY_LOCATION_TYPE_LABELS,
  SHIPLIFY_REQUIRED_ADDRESS_FIELDS,
  SHIPLIFY_STOP_TYPES,
} from './shiplifyConsts';

const normalizeCode = (value: string): string => value.trim().toLowerCase();

export function getShiplifyDestination(
  stops: QuoteFormStop[]
): Undef<QuoteFormStop> {
  const destination = stops.reduce<Undef<QuoteFormStop>>(
    (latest, stop) =>
      latest === undefined || stop.order > latest.order ? stop : latest,
    undefined
  );

  if (!destination?.location) {
    return destination;
  }

  const parsed = parseStopAddressFromLocation(destination.location);
  return parsed
    ? applyParsedStopAddressToQuoteFormStop(destination, parsed)
    : destination;
}

export function getShiplifyStop(
  stops: QuoteFormStop[],
  stopIndex: number,
  options?: { requireLocationLineFullAddress?: boolean }
): Undef<QuoteFormStop> {
  const stop = stops[stopIndex];
  if (!stop) {
    return stop;
  }

  if (options?.requireLocationLineFullAddress) {
    const parsed = parseStopAddressFromLocation(stop.location ?? '');
    return parsed
      ? applyParsedStopAddressToQuoteFormStop(stop, parsed)
      : {
          ...stop,
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zip: '',
          country: undefined,
        };
  }

  if (!stop.location) {
    return stop;
  }

  const parsed = parseStopAddressFromLocation(stop.location);
  return parsed ? applyParsedStopAddressToQuoteFormStop(stop, parsed) : stop;
}

export function formatShiplifyLocationType(value: string): string {
  const normalized = value.trim().toLowerCase();
  return (
    SHIPLIFY_LOCATION_TYPE_LABELS[normalized] ??
    normalized
      .split('-')
      .filter(Boolean)
      .map((word) => word[0]?.toUpperCase() + word.slice(1))
      .join(' ')
  );
}

export function getShiplifyMissingAddressFields(
  destination: Undef<QuoteFormStop>
): string[] {
  if (!destination) {
    return SHIPLIFY_REQUIRED_ADDRESS_FIELDS.map(([, label]) => label);
  }

  return SHIPLIFY_REQUIRED_ADDRESS_FIELDS.filter(([field]) => {
    const value = destination[field];
    return typeof value !== 'string' || value.trim().length === 0;
  }).map(([, label]) => label);
}

export function isShiplifyDestinationComplete(
  destination: Undef<QuoteFormStop>
): boolean {
  return getShiplifyMissingAddressFields(destination).length === 0;
}

export function getShiplifyStopsToCheck(
  stops: QuoteFormStop[],
  lookups: Partial<Record<StopTypes, LTLShiplifyLookupMetadata>>,
  options?: {
    requireLocationLineFullAddress?: boolean;
    // When true, skip stops that already have an Address book externalTMSID.
    skipAddressBookStops?: boolean;
  }
): StopTypes[] {
  return SHIPLIFY_STOP_TYPES.filter((stopType, index) => {
    if (lookups[stopType]) {
      return false;
    }

    const stop = getShiplifyStop(stops, index, options);
    if (options?.skipAddressBookStops && Boolean(stop?.externalTMSID?.trim())) {
      return false;
    }

    return isShiplifyDestinationComplete(stop);
  });
}

export function getShiplifyAddressKey(
  destination: Undef<QuoteFormStop>
): string {
  if (!destination) {
    return '';
  }

  return [
    destination.addressName,
    destination.addressLine1,
    destination.addressLine2,
    destination.city,
    destination.state,
    destination.zip,
    destination.country,
  ]
    .map((value) => value?.trim().toLowerCase() ?? '')
    .join('|');
}

export function getShiplifyAddressKeys(
  stops: QuoteFormStop[]
): Record<StopTypes, string> {
  return Object.fromEntries(
    SHIPLIFY_STOP_TYPES.map((stopType, index) => [
      stopType,
      getShiplifyAddressKey(
        getShiplifyStop(stops, index, {
          requireLocationLineFullAddress: true,
        })
      ),
    ])
  ) as Record<StopTypes, string>;
}

export type ShiplifyRecommendationMatch = {
  accessorial?: LTLAccessorial;
  recommendation: LTLShiplifyRecommendation;
  code?: string;
};

export function formatShiplifyTariffCode(code: string): string {
  return SHIPLIFY_CODE_LABELS[code.trim().toUpperCase()] ?? code;
}

export function formatShiplifyStopType(stopType: StopTypes): string {
  return stopType === StopTypes.Pickup ? 'Pickup' : 'Drop-off';
}

export function shiplifyConfidenceAccuracy(confidence: string): Undef<number> {
  return SHIPLIFY_CONFIDENCE_ACCURACY[confidence.trim().toUpperCase()];
}

function findAccessorialForShiplifyCode(
  code: string,
  accessorials: LTLAccessorial[],
  stopType?: StopTypes
): Undef<LTLAccessorial> {
  const normalizedCode = normalizeCode(code);
  const exact = accessorials.filter(
    (accessorial) => normalizeCode(accessorial.code) === normalizedCode
  );
  if (exact.length === 1) {
    return exact[0];
  }

  const terms = SHIPLIFY_CODE_NAME_TERMS[code.trim().toUpperCase()];
  if (!terms) {
    return undefined;
  }
  const category = stopType === StopTypes.Pickup ? 'pickup' : 'delivery';
  const semantic = accessorials.filter(
    (accessorial) =>
      accessorial.categoryTags?.includes(category) &&
      terms.some((term) => accessorial.name.toLowerCase().includes(term))
  );

  const exactSemantic = semantic.filter((accessorial) =>
    terms.some(
      (term) => accessorial.name.trim().toLowerCase() === term.toLowerCase()
    )
  );
  if (exactSemantic.length === 1) {
    return exactSemantic[0];
  }

  return semantic.length === 1 ? semantic[0] : undefined;
}

export function matchShiplifyRecommendations(
  recommendations: LTLShiplifyRecommendation[],
  accessorials: LTLAccessorial[],
  stopType?: StopTypes
): {
  autoApply: ShiplifyRecommendationMatch[];
  suggestions: ShiplifyRecommendationMatch[];
} {
  const autoApply: ShiplifyRecommendationMatch[] = [];
  const suggestions: ShiplifyRecommendationMatch[] = [];

  recommendations.forEach((recommendation) => {
    const codes = recommendation.codes ?? [];
    if (codes.length === 0) {
      suggestions.push({ recommendation });
      return;
    }

    codes.forEach((code) => {
      const accessorial = findAccessorialForShiplifyCode(
        code,
        accessorials,
        stopType
      );
      const match = { recommendation, accessorial, code };
      if (
        recommendation.decision === LTLShiplifyDecision.AutoApply &&
        accessorial
      ) {
        autoApply.push(match);
      } else {
        suggestions.push(match);
      }
    });
  });

  return { autoApply, suggestions };
}
